import React, { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import DataTable from 'components/ui/data-table';
import LoadingState from '../../shared/LoadingState';

// Reach thresholds behind the build/kill/promote guidance shown in the UI
const LOW_REACH_PCT = 5;

function TrendArrow({ current, prior }) {
  const delta = current - prior;
  if (Math.abs(delta) < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 text-muted-foreground">
        <Minus size={13} /> flat
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span className={cn('inline-flex items-center gap-0.5 font-medium', up ? 'text-success' : 'text-error')}>
      {up ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
      {Math.abs(delta).toFixed(1)}pp
    </span>
  );
}

function verdict(row) {
  if (row.reach_pct < LOW_REACH_PCT && row.prior_reach_pct < LOW_REACH_PCT) {
    return { label: 'Kill candidate', variant: 'destructive' };
  }
  if (row.reach_pct >= 30 && row.median_active_days <= 1) {
    return { label: 'Improve depth', variant: 'warning' };
  }
  if (row.reach_pct < 15 && row.power_gap) {
    return { label: 'Promote', variant: 'info' };
  }
  return null;
}

export default function ProductUsageTab({ adoption, contrast, discovery, rhythm, pages, loading }) {
  const [featureView, setFeatureView] = useState('features');

  const adoptionRows = useMemo(() => {
    const rows = (adoption?.data || []).filter((r) =>
      featureView === 'sub' ? r.is_sub_feature : !r.is_sub_feature
    );
    // Attach power-gap so the verdict can spot discovery problems
    const gaps = {};
    (contrast?.data || []).forEach((c) => { gaps[c.feature] = c.gap_pct; });
    return rows.map((r) => ({ ...r, power_gap: (gaps[r.feature] || 0) > 20 }));
  }, [adoption, contrast, featureView]);

  const adoptionColumns = useMemo(() => [
    {
      accessorKey: 'feature',
      header: 'Feature',
      meta: { cellClassName: 'font-mono text-token-sm' },
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-2">
          {row.original.feature}
          {(() => {
            const v = verdict(row.original);
            return v ? <Badge variant={v.variant}>{v.label}</Badge> : null;
          })()}
        </span>
      ),
    },
    {
      accessorKey: 'reach_pct',
      header: 'Reach (% of subs)',
      meta: { numeric: true },
      cell: ({ row }) => `${row.original.reach_pct.toFixed(1)}%`,
    },
    {
      accessorKey: 'prior_reach_pct',
      header: 'Trend',
      meta: { numeric: true },
      cell: ({ row }) => (
        <TrendArrow current={row.original.reach_pct} prior={row.original.prior_reach_pct} />
      ),
    },
    {
      accessorKey: 'users',
      header: 'Users',
      meta: { numeric: true },
    },
    {
      accessorKey: 'median_active_days',
      header: 'Median active days',
      meta: { numeric: true },
    },
    {
      accessorKey: 'engagement_sec_per_user',
      header: 'Engagement s/user',
      meta: { numeric: true },
      cell: ({ row }) => row.original.engagement_sec_per_user.toFixed(0),
    },
  ], []);

  const contrastColumns = useMemo(() => [
    { accessorKey: 'feature', header: 'Feature', meta: { cellClassName: 'font-mono text-token-sm' } },
    {
      accessorKey: 'power_reach_pct',
      header: 'Power users',
      meta: { numeric: true },
      cell: ({ row }) => `${row.original.power_reach_pct.toFixed(0)}%`,
    },
    {
      accessorKey: 'casual_reach_pct',
      header: 'Everyone else',
      meta: { numeric: true },
      cell: ({ row }) => `${row.original.casual_reach_pct.toFixed(0)}%`,
    },
    {
      accessorKey: 'gap_pct',
      header: 'Gap',
      meta: { numeric: true },
      cell: ({ row }) => (
        <span className={cn('font-semibold', row.original.gap_pct > 30 && 'text-primary')}>
          {row.original.gap_pct.toFixed(0)}pp
        </span>
      ),
    },
  ], []);

  const discoveryColumns = useMemo(() => [
    { accessorKey: 'feature', header: 'Feature', meta: { cellClassName: 'font-mono text-token-sm' } },
    {
      accessorKey: 'week1_pct',
      header: 'Found in week 1',
      meta: { numeric: true },
      cell: ({ row }) => `${row.original.week1_pct.toFixed(0)}% (${row.original.week1_users})`,
    },
    {
      accessorKey: 'ever_pct',
      header: 'Found ever',
      meta: { numeric: true },
      cell: ({ row }) => `${row.original.ever_pct.toFixed(0)}% (${row.original.ever_users})`,
    },
  ], []);

  // Rhythm: feature rows with during/between share
  const rhythmData = useMemo(() => {
    const byFeature = {};
    (rhythm?.data || []).forEach((r) => {
      if (r.is_sub_feature) return;
      const f = byFeature[r.feature] || { feature: r.feature, during: 0, between: 0 };
      f[r.gameweek_phase === 'during' ? 'during' : 'between'] += r.user_days;
      byFeature[r.feature] = f;
    });
    return Object.values(byFeature)
      .map((f) => ({ ...f, total: f.during + f.between }))
      .filter((f) => f.total >= 20)
      .map((f) => ({ ...f, during_pct: Math.round((100 * f.during) / f.total) }))
      .sort((a, b) => b.during_pct - a.during_pct);
  }, [rhythm]);

  // Engagement league table straight from wa_page_metrics
  const engagementData = useMemo(() => {
    const byPage = {};
    (pages || []).forEach((p) => {
      const key = `${p.property}:${p.page_path}`;
      const cur = byPage[key] || {
        page: p.page_path, property: p.property, pageviews: 0, users: 0, engagement: 0,
      };
      cur.pageviews += p.pageviews;
      cur.users += p.unique_users;
      cur.engagement += p.avg_engagement_seconds * p.pageviews;
      byPage[key] = cur;
    });
    return Object.values(byPage)
      .filter((p) => p.users >= 50)
      .map((p) => ({
        ...p,
        pv_per_user: p.users ? (p.pageviews / p.users).toFixed(1) : 0,
        eng_per_pv: p.pageviews ? (p.engagement / p.pageviews).toFixed(1) : 0,
      }))
      .sort((a, b) => b.eng_per_pv - a.eng_per_pv)
      .slice(0, 20);
  }, [pages]);

  const engagementColumns = useMemo(() => [
    { accessorKey: 'page', header: 'Page', meta: { cellClassName: 'font-mono text-token-sm' } },
    { accessorKey: 'property', header: 'Site' },
    { accessorKey: 'users', header: 'User-days', meta: { numeric: true } },
    { accessorKey: 'pv_per_user', header: 'PV / user-day', meta: { numeric: true } },
    { accessorKey: 'eng_per_pv', header: 'Engagement s/pv', meta: { numeric: true } },
  ], []);

  if (!adoption && loading !== false) {
    return <LoadingState message="Loading product usage..." />;
  }

  return (
    <div className="w-full min-w-0">
      {/* Feature Adoption Matrix */}
      <div className="mb-4">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h3 className="text-token-xl font-semibold">Feature Adoption</h3>
          <ToggleGroup type="single" size="sm" value={featureView} onValueChange={(v) => v && setFeatureView(v)}>
            <ToggleGroupItem value="features">Pages</ToggleGroupItem>
            <ToggleGroupItem value="sub">Tabs</ToggleGroupItem>
          </ToggleGroup>
          <span className="ml-auto text-token-xs text-muted-foreground">
            Reach = % of {adoption?.active_subscribers?.toLocaleString() || '—'} active subscribers, last 28d vs prior 28d
          </span>
        </div>
        <DataTable
          columns={adoptionColumns}
          data={adoptionRows}
          maxHeight={420}
          emptyMessage="No adoption data yet — needs a wa_user_feature_daily backfill."
        />
        <p className="mt-1 text-token-xs text-muted-foreground">
          Kill candidate: reach &lt;{LOW_REACH_PCT}% both periods · Improve depth: broad but shallow ·
          Promote: low reach overall but power users rely on it (discovery problem)
        </p>
      </div>

      {/* Power vs casual + discovery, side by side */}
      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div>
          <h3 className="mb-2 text-token-xl font-semibold">
            Power vs Casual
            <span className="ml-2 text-token-xs font-normal text-muted-foreground">
              top decile ({contrast?.power_users || 0}) vs rest ({contrast?.casual_users || 0}) — the gap is the retention playbook
            </span>
          </h3>
          <DataTable
            columns={contrastColumns}
            data={(contrast?.data || []).filter((r) => !r.is_sub_feature)}
            maxHeight={360}
            emptyMessage="No contrast data."
          />
        </div>
        <div>
          <h3 className="mb-2 text-token-xl font-semibold">
            New Subscriber Discovery
            <span className="ml-2 text-token-xs font-normal text-muted-foreground">
              {discovery?.cohort_size || 0} subs started in last {discovery?.cohort_days || 60}d
            </span>
          </h3>
          <DataTable
            columns={discoveryColumns}
            data={(discovery?.data || []).filter((r) => !r.is_sub_feature)}
            maxHeight={360}
            emptyMessage="No discovery data."
          />
        </div>
      </div>

      {/* Engagement league table */}
      <div className="mb-4">
        <h3 className="mb-2 text-token-xl font-semibold">
          Engagement League
          <span className="ml-2 text-token-xs font-normal text-muted-foreground">
            depth per pageview — dwell pages vs pass-through hubs (SPA tabs undercount dwell)
          </span>
        </h3>
        <DataTable
          columns={engagementColumns}
          data={engagementData}
          maxHeight={360}
          emptyMessage="No page data."
        />
      </div>

      {/* Gameweek rhythm */}
      {rhythmData.length > 0 && (
        <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-4">
          <h3 className="mb-2 text-token-xl font-semibold">
            Gameweek Rhythm
            <span className="ml-2 text-token-xs font-normal text-muted-foreground">
              % of usage during gameweeks — fills up as the season runs
            </span>
          </h3>
          <ResponsiveContainer width="100%" height={Math.max(200, rhythmData.length * 28)}>
            <BarChart data={rhythmData} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" />
              <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
              <YAxis dataKey="feature" type="category" tick={{ fontSize: 11 }} width={120} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="during_pct" fill="#254A73" name="% during gameweeks" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
